// I18nProvider — bọc provider của Lingui, thêm 2 việc mà Lingui không lo:
//
//  1. CHỐNG NHÁY CHỮ Ở LẦN VẼ ĐẦU. Khi ngôn ngữ khác tiếng Việt và catalog cụm chưa về,
//     hiện `fallback` thay vì vẽ page — không có bước này thì user tiếng Anh thấy tiếng Việt
//     loé lên rồi mới đổi. User tiếng Việt KHÔNG bao giờ dính gate này (catalog vi tĩnh).
//
//     ⚠️ CHỈ LẦN ĐẦU — đây là bản vá cho lỗi "web tự tải lại liên tục" (sếp Phú 16/08, có
//     video). Trước đây gate chạy MỖI LẦN `pending > 0`, mà `pending` tăng mỗi khi một cụm
//     catalog mới bắt đầu tải. `prefetchAllRoutes()` lại tải NGẦM toàn bộ route sau khi vào
//     app (cách nhau 180ms), mỗi chunk kéo theo một shim `registerCluster` mới ⇒ với 35 cụm,
//     `waiting` bật/tắt tới 35 lần. Mỗi lần bật, `children` bị THÁO khỏi cây rồi gắn lại:
//     trang đang mở mount lại từ đầu, gọi lại API, hiện lại vòng xoay của chính nó. Nhìn y
//     như trang tự tải lại — nhưng favicon không quay và thanh bên không nháy, vì tài liệu
//     KHÔNG hề nạp lại. Và chỉ xảy ra ở EN/FR/ES, vì tiếng Việt không qua gate.
//
//     Sau lần vẽ đầu, việc chờ catalog của trang/drawer mở sau do `lazyWithReload` lo: nó
//     giữ module ở Suspense của CHÍNH route đó tới khi catalog cụm về (whenCatalogsLoaded
//     trong runtime.js). ⚠️ Đừng trông vào «catalog về muộn thì Lingui vẽ lại»: bản production
//     không có chữ inline nên vẽ sớm là ra MÃ BĂM, và chỗ đọc `i18n._(msg)` lúc render không
//     đăng ký với Lingui nên giữ nguyên mã băm (sự cố /me 12/10/2026).
//
//  2. Vẽ lại khi runtime đổi ngôn ngữ (subscribe → useSyncExternalStore).
//
//  3. DỰNG LẠI cây con khi đổi ngôn ngữ (`key={locale}`). Vẽ lại KHÔNG đủ: rất nhiều chỗ
//     tính nhãn đã dịch trong `useMemo` mà deps không có ngôn ngữ, nên sau khi đổi vẫn giữ
//     chữ cũ → nửa màn hình Anh nửa Việt. Trước đây các nút đổi ngôn ngữ né chuyện này bằng
//     `window.location.reload()` — tải lại TOÀN BỘ app (mất vài giây, tải lại hết bundle) chỉ
//     để dọn mấy giá trị memo. Đổi khoá thì React tự dựng lại cây, memo cũ chết theo, mà
//     không phải nạp lại trang (sếp Phú 16/08: "chuyển sang tiếng anh vẫn reload").
//
// Host mount 1 lần ở gốc cây, NGOÀI Router:
//   <I18nProvider fallback={<AppLoading />}> <App /> </I18nProvider>

import { Fragment, useRef, useSyncExternalStore } from 'react';
import { I18nProvider as LinguiProvider } from '@lingui/react';
import { i18n, subscribe, getPendingCount, getLocale } from './runtime.js';
import { SOURCE_LOCALE } from './locales.js';

function snapshot() {
  // Gộp locale + pending thành 1 chuỗi để useSyncExternalStore so sánh được bằng ===
  return `${getLocale()}:${getPendingCount()}`;
}

export default function I18nProvider({ children, fallback = null }) {
  useSyncExternalStore(subscribe, snapshot, snapshot);

  const locale = getLocale();
  // Đã vẽ được children lần nào chưa — sau lần đó thì KHÔNG bao giờ tháo cây ra nữa.
  const daVeLanDau = useRef(false);
  const waiting = locale !== SOURCE_LOCALE && getPendingCount() > 0 && !daVeLanDau.current;
  if (!waiting) daVeLanDau.current = true;

  return (
    <LinguiProvider i18n={i18n}>
      {/* key = ngôn ngữ: đổi ngôn ngữ ⇒ dựng lại cây con, dọn sạch memo giữ chữ cũ. Cùng
          một ngôn ngữ thì key không đổi nên KHÔNG dựng lại gì thêm ở lượt vẽ thường. */}
      {waiting ? fallback : <Fragment key={locale}>{children}</Fragment>}
    </LinguiProvider>
  );
}
